import type { RegistrationData } from '@/types/registration';
import { wregEligStep, wregEligSwitch } from '@/components/webreg/wregEligibility';
import { wregAlignNeedsAttestation } from '@/components/webreg/wregAlignment';
import { wregHasShipped } from '@/components/webreg/wregFlowState';
import type { WregStep } from '@/components/webreg/wregSteps';
import { FIRST_NAME_MAX_LENGTH, LAST_NAME_MAX_LENGTH, nameIsValid } from '@/utils/name';

export type WregGateCtx = {
  data: RegistrationData;
  devEnabled: boolean;
};

export type WregGate = (ctx: WregGateCtx) => boolean;

// eligResumable is passed here too, not only at the point of navigation: this recomputes the step
// on every gate check, so without it a resumable already-aligned member is routed to `screener`
// and then immediately bounced off it.
const reachedScreener = (data: RegistrationData): boolean => {
  const code = data.wreg.eligCode;
  return code !== null && wregEligStep(code, data.wreg.eligResumable) === 'screener';
};

const needsSwitch = (data: RegistrationData): boolean =>
  (data.wreg.eligCode !== null && wregEligSwitch(data.wreg.eligCode)) ||
  wregAlignNeedsAttestation(data.wreg.alignCode ?? undefined);

const exitIs = (step: WregStep): WregGate => ({ data }) => data.wreg.exit === step;

// The MBI is no longer persisted (RND-38783), so every screen between step 1 and the alignment
// call has to check it: a refresh drops it, and a gate that passes without it walks the member to
// the address CTA where alignment posts an empty MBI and CMS answers 422 on the last click of the
// flow. Bouncing to the resume step instead sends them back to /medicare-number with one field to
// re-type.
const hasMbi = (data: RegistrationData): boolean => data.medicareCoverage.mbi !== '';

// Revalidated rather than checked for '', so a name saved before the rule existed goes back to step 1.
const named = (data: RegistrationData): boolean =>
  nameIsValid(data.medicareCoverage.firstName, FIRST_NAME_MAX_LENGTH) &&
  nameIsValid(data.medicareCoverage.lastName, LAST_NAME_MAX_LENGTH) &&
  data.wreg.intakeTerms;

const namedAndReachable = (data: RegistrationData): boolean =>
  named(data) && data.account.phoneNumber !== '' && data.account.email !== '';

export const WREG_GATE: Record<WregStep, WregGate> = {
  intake: () => true,
  'intake-contact': ({ data }) => named(data),
  'intake-about': ({ data }) => namedAndReachable(data),
  'intake-mbi': ({ data }) =>
    namedAndReachable(data) &&
    data.medicareCoverage.dateOfBirth !== '' &&
    data.wreg.intakeGender !== '',
  screener: ({ data }) => hasMbi(data) && reachedScreener(data),
  tracks: ({ devEnabled }) => devEnabled,
  agreement: ({ devEnabled }) => devEnabled,
  switch: ({ data }) => hasMbi(data) && data.selectedTracks.length > 0 && needsSwitch(data),
  // Gated on the screener's write, not on consentAgreed. Consent moved to the step-1 checkbox in
  // RND-38229, and a gate that reads a write from two steps earlier bounces anyone who refreshes
  // mid-flow before that write lands.
  labs: ({ data }) => hasMbi(data) && data.selectedTracks.length > 0,
  monitor: ({ data }) => hasMbi(data) && data.labsCompleted === true,
  allset: ({ data }) => wregHasShipped(data) || data.wreg.exit === 'x-labspending',
  'x-notelig': exitIs('x-notelig'),
  'x-already': exitIs('x-already'),
  'x-control': exitIs('x-control'),
  'x-mismatch': exitIs('x-mismatch'),
  'x-noqualify': exitIs('x-noqualify'),
  'x-error': exitIs('x-error'),
  'x-switchok': exitIs('x-switchok'),
  'x-outreach': exitIs('x-outreach'),
  'x-labspending': exitIs('x-labspending'),
  'x-declined': exitIs('x-declined'),
};

export const wregStepIsAccessible = (step: WregStep, ctx: WregGateCtx): boolean =>
  WREG_GATE[step](ctx);
